import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { ExtendedUser, License } from "../../store/types";
import { userColumns } from "../../components/DatatableTemplates/UserTableTemplates";
import { Container } from "../../components/Container/Container";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import UserDialogs from "../../components/Dialogs/UserDialogs";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { IconPlus, IconSearch, IconSend } from "../../assets/icons/Icon";
import {
  PageName,
  FlexLineJustify,
  FlexLineGapSmall,
  Label,
  InputContainer,
  Tag,
} from "../../styles/PageLayoutStyles.styles";

const Users: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const {
    handlePostUser,
    handleUpdateUser,
    handleDeleteUser,
    handleSendActivationLink,
    users,
    refetchData,
    toastRef,
  } = useUsersAPI();
  const { companiesDropdownOpts } = useCompaniesAPI();
  const { licenses } = useLicensesAPI();
  const [tableData, setTableData] = useState<ExtendedUser[] | null>(null);

  const [selectedUser, setSelectedUser] = useState<ExtendedUser | null>();
  const [searchValue, setSearchValue] = useState<string>("");
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<"create" | "edit" | "delete">(
    "create"
  );

  useEffect(() => {
    if (users && licenses) {
      const formatted = users.map((user) => {
        const userLicensesCount = licenses.filter(
          (lic: License) => lic.userId === user.id
        ).length;
        return {
          ...user,
          fullName: `${user.lastName ?? ""} ${user.name ?? ""} ${
            user.patronymic ?? ""
          }`.trim(),
          organization: user.organizationName,
          phone: user.phone
            ? String(user.phone).replace(/\D/g, "").slice(-10)
            : user.phone,
          licenses: userLicensesCount,
        };
      });
      setTableData(formatted);
    } else {
      setTableData(null);
    }
  }, [users, licenses]);

  const filteredData = useMemo(() => {
    if (!searchValue.trim() || !tableData) return tableData;
    const lowerSearch = searchValue.toLowerCase();
    return tableData.filter((user) => {
      return (
        user.fullName?.toLowerCase().includes(lowerSearch) ||
        user.organizationName?.toLowerCase().includes(lowerSearch) ||
        user.email?.toLowerCase().includes(lowerSearch) ||
        user.phone?.toLowerCase().includes(lowerSearch) ||
        user.licenses?.toString().includes(lowerSearch)
      );
    });
  }, [tableData, searchValue]);

  const handleView = (user: ExtendedUser) => {
    navigate(`/users/${user.id}`);
  };

  const handleCreate = () => {
    setSelectedUser(null);

    if (isMobile) {
      navigate("/users/form?mode=create");
    } else {
      setDialogType("create");
      setDialogVisible(true);
    }
  };

  const handleEdit = (user: ExtendedUser) => {
    setSelectedUser(user);

    if (isMobile) {
      navigate(`/users/form?mode=edit&id=${user.id}`);
    } else {
      setDialogType("edit");
      setDialogVisible(true);
    }
  };

  const handleDelete = (user: ExtendedUser) => {
    setSelectedUser(user);

    if (isMobile) {
      navigate(`/users/form?mode=delete&id=${user.id}`);
    } else {
      setDialogType("delete");
      setDialogVisible(true);
    }
  };

  const handleDialogHide = () => {
    setDialogVisible(false);
  };

  const handleSelectionChange = (selected: ExtendedUser | null) => {
    setSelectedUser(selected);
  };

  const columns = useMemo(() => {
    const activationColumn = {
      sortable: false,
      field: "activation",
      header: "",
      body: (rowData: ExtendedUser) => {
        if (rowData.isActivated) return null;
        return (
          <Button
            text
            onClick={(e) => {
              e.stopPropagation();
              handleSendActivationLink(rowData.id);
            }}
            tooltip="Отправить ссылку активации"
            tooltipOptions={{ position: "top" }}
          >
            <IconSend size={16} />
          </Button>
        );
      },
    };
    return [...userColumns, activationColumn];
  }, [handleSendActivationLink]);

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <FlexLineJustify>
        <PageName>
          <H2>Пользователи</H2>
          <Tag>
            {isMobile ? "" : "Количество:"} {users?.length.toString() ?? "..."}
          </Tag>
        </PageName>

        <FlexLineGapSmall>
          <Button onClick={handleCreate}>
            <IconPlus size={20} />
            <Label>Новый пользователь</Label>
          </Button>
          <InputContainer>
            <CustomInputText
              value={searchValue}
              setValue={setSearchValue}
              placeholder="Поиск..."
              icon={<IconSearch size={20} />}
            />
          </InputContainer>
        </FlexLineGapSmall>
      </FlexLineJustify>
      <CustomDatatable
        data={filteredData ?? []}
        columns={columns}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRowDoubleClick={handleView}
        selected={selectedUser}
        onSelectionChange={handleSelectionChange}
        getRowId={(user) => user.id}
        sortable
      />
      <UserDialogs
        visible={dialogVisible}
        onHide={handleDialogHide}
        dialogType={dialogType}
        user={selectedUser!}
        handleAddUser={handlePostUser}
        handleEditUser={handleUpdateUser}
        handleDeleteUser={handleDeleteUser}
        refetchData={refetchData}
        companiesOptions={companiesDropdownOpts}
      />
    </Container>
  );
};

export default Users;
